/**
 * Accessories module — standalone catalog, inventory, retail & B2B billing.
 *
 * Layout: 5 tabs (Catalog · Stock · Retail Bill · B2B Bill · Reports).
 * Backend routes are under /api/accessories/*.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import BranchSelect from "@/components/BranchSelect";
import buildAccessoryInvoiceHtml from "@/components/accessoryInvoiceHtml";
import useBranches from "@/hooks/useBranches";
import { useAuth } from "@/context/AuthContext";
import {
  Package as PackageIcon,
  Plus,
  UploadSimple,
  DownloadSimple,
  Receipt,
  Storefront,
  ChartBar,
  Trash,
  PencilSimple,
  ArrowsLeftRight,
  Truck,
} from "@phosphor-icons/react";

const CATEGORY_OPTIONS = ["helmet", "guard", "cover", "kit", "spare", "electrical", "other"];

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

// Fetch the full invoice (branch + sub-dealer + dealership letterhead) and
// open the printable GST invoice in a new window.
async function printAccessoryInvoice(invoiceId) {
  try {
    const { data } = await api.get(`/accessories/invoices/${invoiceId}`);
    const w = window.open("", "_blank", "width=900,height=700");
    if (!w) { toast.error("Popup blocked — allow popups to print"); return; }
    w.document.write(buildAccessoryInvoiceHtml(data));
    w.document.close();
  } catch (e) {
    toast.error(formatApiError(e));
  }
}

const TABS = [
  { id: "catalog", label: "Catalog", icon: PackageIcon },
  { id: "stock", label: "Stock", icon: Storefront },
  { id: "retail", label: "Retail Bill", icon: Receipt },
  { id: "b2b", label: "B2B Bill (Sub-Dealer)", icon: Receipt },
  { id: "reports", label: "Reports", icon: ChartBar },
];

export default function AccessoriesPage() {
  const [tab, setTab] = useState("catalog");
  return (
    <div className="p-6 space-y-6">
      <PageHeader
        eyebrow="11 / Accessories"
        title="Accessories"
        subtitle="Inventory, pricing and billing for helmets, guards, covers and more — retail counter + sub-dealer wholesale."
      />
      <div className="flex flex-wrap gap-1 border-b border-canvas-line">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`accessories-tab-${t.id}`}
              className={`flex items-center gap-1.5 px-4 py-2 text-xs font-mono uppercase tracking-wider border-b-2 -mb-px ${
                active
                  ? "border-ink text-ink"
                  : "border-transparent text-ink-muted hover:text-ink"
              }`}
            >
              <Icon size={13} weight="bold" />
              {t.label}
            </button>
          );
        })}
      </div>
      {tab === "catalog" && <CatalogTab />}
      {tab === "stock" && <StockTab />}
      {tab === "retail" && <RetailBillTab />}
      {tab === "b2b" && <B2BBillTab />}
      {tab === "reports" && <ReportsTab />}
    </div>
  );
}

// =============================================================
// Catalog
// =============================================================
function CatalogTab() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [editing, setEditing] = useState(null);   // sku object or {} for new
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (category) params.category = category;
      const { data } = await api.get("/accessories/catalog", { params });
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [search, category]);

  useEffect(() => { load(); }, [load]);

  const del = async (r) => {
    if (!window.confirm(`Delete ${r.part_no}? (Deactivates if history exists)`)) return;
    try {
      await api.delete(`/accessories/catalog/${r.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <input
          data-testid="accessories-catalog-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search part no / description…"
          className="px-3 py-2 bg-canvas border border-canvas-line text-sm w-full sm:w-80 focus:outline-none focus:border-ink"
        />
        <select
          data-testid="accessories-category-filter"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
        >
          <option value="">All categories</option>
          {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <div className="ml-auto flex items-center gap-2">
          <button
            data-testid="accessories-import-btn"
            onClick={() => setImporting(true)}
            className="border border-canvas-line px-3 py-2 text-xs font-mono uppercase tracking-wider hover:border-ink flex items-center gap-1.5"
          >
            <UploadSimple size={12} weight="bold" /> Import CSV
          </button>
          <button
            data-testid="accessories-new-btn"
            onClick={() => setEditing({})}
            className="bg-ink text-white px-3 py-2 text-xs font-mono uppercase tracking-wider hover:bg-brand flex items-center gap-1.5"
          >
            <Plus size={12} weight="bold" /> New
          </button>
        </div>
      </div>
      <div className="border border-canvas-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-canvas-panel border-b border-canvas-line">
            <tr>
              {["Part #", "Description", "Model", "Category", "HSN", "NDP", "GST %", "AD MRP", "MRP", ""].map((h) => (
                <th key={h} className="px-3 py-2 font-mono text-[10px] uppercase text-left">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={10} className="px-3 py-10 text-center text-ink-muted text-xs">No accessories. Add a SKU or import from CSV.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-b border-canvas-line/50" data-testid={`accessories-row-${r.part_no}`}>
                <td className="px-3 py-2 font-mono font-semibold">{r.part_no}</td>
                <td className="px-3 py-2">{r.description}</td>
                <td className="px-3 py-2 font-mono text-[11px]">{r.model || "—"}</td>
                <td className="px-3 py-2 font-mono text-[11px] uppercase">{r.category}</td>
                <td className="px-3 py-2 font-mono text-[11px]">{r.hsn_code || "—"}</td>
                <td className="px-3 py-2 font-mono">{inr(r.ndp)}</td>
                <td className="px-3 py-2 font-mono">{r.gst_pct}%</td>
                <td className="px-3 py-2 font-mono">{inr(r.ad_mrp)}</td>
                <td className="px-3 py-2 font-mono font-semibold">{inr(r.mrp)}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button onClick={() => setEditing(r)} data-testid={`accessories-edit-${r.part_no}`} className="p-1 hover:bg-canvas-panel"><PencilSimple size={14} /></button>
                  <button onClick={() => del(r)} className="p-1 text-rose-700 hover:bg-rose-50"><Trash size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <CatalogEditor sku={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {importing && <ImportDialog onClose={() => setImporting(false)} onDone={() => { setImporting(false); load(); }} />}
    </div>
  );
}

function CatalogEditor({ sku, onClose, onSaved }) {
  const isEdit = !!sku;
  const [form, setForm] = useState(sku || {
    part_no: "", description: "", model: "", category: "other",
    hsn_code: "", moq: 1, ndp: 0, gst_pct: 18,
    invoice_price: 0, ad_mrp: 0, mrp: 0, active: true,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, moq: Number(form.moq) || 1 };
      ["ndp", "gst_pct", "invoice_price", "ad_mrp", "mrp"].forEach((k) => { payload[k] = Number(form[k]) || 0; });
      if (isEdit) {
        await api.put(`/accessories/catalog/${sku.id}`, payload);
      } else {
        await api.post("/accessories/catalog", payload);
      }
      toast.success(isEdit ? "Updated" : "Created");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center p-4" data-testid="accessories-editor">
      <form onSubmit={save} className="bg-canvas border border-canvas-line max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="border-b border-canvas-line px-6 py-4">
          <div className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Accessory</div>
          <div className="text-lg font-semibold">{isEdit ? sku.part_no : "New SKU"}</div>
        </div>
        <div className="p-6 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Part No" required value={form.part_no} onChange={(v) => set("part_no", v.toUpperCase())} testId="editor-part-no" mono />
            <Field label="HSN Code" value={form.hsn_code} onChange={(v) => set("hsn_code", v)} mono />
          </div>
          <Field label="Description" required value={form.description} onChange={(v) => set("description", v)} testId="editor-description" />
          <div className="grid grid-cols-3 gap-3">
            <Field label="Model" value={form.model} onChange={(v) => set("model", v)} placeholder="NTORQ / IQUBE / …" />
            <SelectField label="Category" value={form.category} onChange={(v) => set("category", v)} options={CATEGORY_OPTIONS} />
            <Field label="MOQ" type="number" value={form.moq} onChange={(v) => set("moq", v)} mono />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="NDP (₹)" type="number" value={form.ndp} onChange={(v) => set("ndp", v)} mono />
            <Field label="GST %" type="number" value={form.gst_pct} onChange={(v) => set("gst_pct", v)} mono />
            <Field label="Invoice Price (₹)" type="number" value={form.invoice_price} onChange={(v) => set("invoice_price", v)} mono />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="AD MRP (Sub-Dealer, ₹)" type="number" value={form.ad_mrp} onChange={(v) => set("ad_mrp", v)} mono testId="editor-ad-mrp" />
            <Field label="MRP (Retail, ₹)" type="number" value={form.mrp} onChange={(v) => set("mrp", v)} mono testId="editor-mrp" />
          </div>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} />
            Active (visible for billing)
          </label>
        </div>
        <div className="border-t border-canvas-line px-6 py-3 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-canvas-line text-sm hover:border-ink">Cancel</button>
          <button type="submit" disabled={saving} data-testid="editor-save" className="px-4 py-2 bg-ink text-white text-sm font-semibold hover:bg-brand disabled:opacity-50">
            {saving ? "Saving…" : (isEdit ? "Save" : "Create")}
          </button>
        </div>
      </form>
    </div>
  );
}

function ImportDialog({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const doImport = async (dryRun) => {
    if (!file) { toast.error("Choose a CSV file first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/accessories/import?dry_run=${dryRun}`, fd);
      setResult(data);
      if (!dryRun) {
        toast.success(`Imported ${data.inserted} new, updated ${data.updated}`);
        onDone();
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center p-4" data-testid="accessories-import-dialog">
      <div className="bg-canvas border border-canvas-line max-w-md w-full">
        <div className="border-b border-canvas-line px-6 py-4">
          <div className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Import CSV</div>
          <div className="text-lg font-semibold">Bulk Upload Accessories</div>
        </div>
        <div className="p-6 space-y-3 text-sm">
          <div className="text-xs text-ink-muted">
            Required columns: <span className="font-mono">PART NO, PART DESCRIPTION</span>.
            Optional: <span className="font-mono">MODEL, HSN CODE, NDP, DLR GST %, INVOICE PRICE, AD MRP, MRP, CATEGORY</span>.
            Existing part numbers get upserted.
          </div>
          <input
            type="file"
            accept=".csv"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="accessories-import-file"
            className="text-xs"
          />
          {result && (
            <div className="border border-canvas-line bg-canvas-panel p-3 font-mono text-[11px]" data-testid="accessories-import-result">
              Inserted: <b>{result.inserted}</b> · Updated: <b>{result.updated}</b> · Errors: <b>{result.errors?.length || 0}</b>
              {result.errors?.length ? (
                <ul className="mt-1 list-disc pl-4 text-rose-700">
                  {result.errors.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              ) : null}
            </div>
          )}
        </div>
        <div className="border-t border-canvas-line px-6 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-canvas-line text-sm hover:border-ink">Close</button>
          <button onClick={() => doImport(true)} disabled={busy || !file} className="px-3 py-2 border border-canvas-line text-xs font-mono uppercase hover:border-ink disabled:opacity-40">
            Dry-Run
          </button>
          <button
            onClick={() => doImport(false)}
            disabled={busy || !file}
            data-testid="accessories-import-commit"
            className="px-4 py-2 bg-ink text-white text-sm font-semibold hover:bg-brand disabled:opacity-50"
          >
            {busy ? "Working…" : "Import"}
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================
// Stock
// =============================================================
function StockTab() {
  const { user } = useAuth();
  const { branches } = useBranches({ activeOnly: true });
  const isAdmin = user?.role === "admin";
  const [branchId, setBranchId] = useState(user?.branch_id || "");
  const [rows, setRows] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [showOpening, setShowOpening] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [showPurchase, setShowPurchase] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (branchId) params.branch_id = branchId;
      const { data } = await api.get("/accessories/stock", { params });
      setRows(data.rows || []);
      const pur = await api.get("/accessories/purchases", { params: { limit: 10 } });
      setPurchases(pur.data.rows || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [branchId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {isAdmin && (
          <select
            data-testid="accessories-stock-branch"
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            className="px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
          >
            <option value="">All branches</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => setShowPurchase(true)}
            data-testid="accessories-purchase-btn"
            className="bg-emerald-700 text-white px-3 py-2 text-xs font-mono uppercase tracking-wider hover:bg-emerald-800 flex items-center gap-1.5"
          >
            <Truck size={12} weight="bold" /> Receive from TVS
          </button>
          <button
            onClick={() => setShowTransfer(true)}
            data-testid="accessories-transfer-btn"
            className="border border-canvas-line px-3 py-2 text-xs font-mono uppercase tracking-wider hover:border-ink flex items-center gap-1.5"
          >
            <ArrowsLeftRight size={12} weight="bold" /> Transfer Stock
          </button>
          <button
            onClick={() => setShowOpening(true)}
            data-testid="accessories-opening-btn"
            className="bg-ink text-white px-3 py-2 text-xs font-mono uppercase tracking-wider hover:bg-brand flex items-center gap-1.5"
          >
            <Plus size={12} weight="bold" /> Opening Stock Entry
          </button>
        </div>
      </div>
      <div className="border border-canvas-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-canvas-panel border-b border-canvas-line">
            <tr>
              {["Part #", "Description", "Branch", "On Hand", "Value @ NDP"].map((h) => (
                <th key={h} className="px-3 py-2 font-mono text-[10px] uppercase text-left">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={5} className="px-3 py-10 text-center text-ink-muted text-xs">No on-hand stock. Use "Opening Stock Entry" to load quantities.</td></tr>
            ) : rows.map((r) => (
              <tr key={`${r.sku_id}-${r.branch_id}`} className="border-b border-canvas-line/50" data-testid={`stock-row-${r.accessory?.part_no}`}>
                <td className="px-3 py-2 font-mono font-semibold">{r.accessory?.part_no}</td>
                <td className="px-3 py-2">{r.accessory?.description}</td>
                <td className="px-3 py-2">{r.branch?.name || r.branch_id}</td>
                <td className="px-3 py-2 font-mono font-semibold">{r.quantity}</td>
                <td className="px-3 py-2 font-mono">{inr((r.accessory?.ndp || 0) * (r.quantity || 0))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {purchases.length > 0 && (
        <div className="border border-canvas-line" data-testid="recent-receipts">
          <div className="px-3 py-2 border-b border-canvas-line font-mono text-[10px] uppercase tracking-widest text-ink-muted flex items-center gap-1.5">
            <Truck size={12} weight="bold" /> Recent TVS Receipts @ Stock Yard ({purchases.length})
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-canvas-panel border-b border-canvas-line">
                <tr>
                  {["Invoice #", "Date", "Lines", "Units", "Value @ NDP"].map((h) => (
                    <th key={h} className="px-3 py-2 font-mono text-[10px] uppercase text-left">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {purchases.map((p) => (
                  <tr key={p.id} className="border-b border-canvas-line/50" data-testid={`purchase-row-${p.invoice_no}`}>
                    <td className="px-3 py-2 font-mono font-semibold">{p.invoice_no}</td>
                    <td className="px-3 py-2 font-mono text-[11px]">{p.invoice_date}</td>
                    <td className="px-3 py-2 font-mono">{(p.lines || []).length}</td>
                    <td className="px-3 py-2 font-mono">{p.total_qty}</td>
                    <td className="px-3 py-2 font-mono font-semibold">{inr(p.total_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {showOpening && <OpeningStockDialog onClose={() => setShowOpening(false)} onSaved={() => { setShowOpening(false); load(); }} />}
      {showTransfer && <TransferStockDialog onClose={() => setShowTransfer(false)} onSaved={() => { setShowTransfer(false); load(); }} />}
      {showPurchase && <PurchaseDialog onClose={() => setShowPurchase(false)} onSaved={() => { setShowPurchase(false); load(); }} />}
    </div>
  );
}

function PurchaseDialog({ onClose, onSaved }) {
  const [invoiceNo, setInvoiceNo] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().slice(0, 10));
  const [supplier, setSupplier] = useState("TVS");
  const [note, setNote] = useState("");
  const [catalog, setCatalog] = useState([]);
  const [lines, setLines] = useState({});   // sku_id -> {qty, ndp}
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/accessories/catalog", { params: { active_only: true } })
      .then((r) => setCatalog(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const visible = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return catalog;
    return catalog.filter((c) =>
      (c.part_no || "").toLowerCase().includes(s) || (c.description || "").toLowerCase().includes(s)
    );
  }, [catalog, search]);

  const setLine = (sku, k, v) =>
    setLines((L) => ({ ...L, [sku.id]: { qty: L[sku.id]?.qty ?? "", ndp: L[sku.id]?.ndp ?? sku.ndp ?? 0, [k]: v } }));

  const touched = useMemo(
    () => Object.entries(lines).filter(([, v]) => Number(v.qty) > 0),
    [lines]
  );
  const totals = useMemo(() => {
    let qty = 0, val = 0;
    touched.forEach(([skuId, v]) => {
      const sku = catalog.find((c) => c.id === skuId);
      const ndp = Number(v.ndp) || Number(sku?.ndp) || 0;
      qty += Number(v.qty);
      val += ndp * Number(v.qty);
    });
    return { qty, val };
  }, [touched, catalog]);

  const submit = async () => {
    if (!invoiceNo.trim()) { toast.error("Enter the TVS invoice number"); return; }
    if (!touched.length) { toast.error("Enter received qty for at least one SKU"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/accessories/stock/purchase", {
        invoice_no: invoiceNo.trim(),
        invoice_date: invoiceDate,
        supplier: supplier.trim() || "TVS",
        note,
        lines: touched.map(([sku_id, v]) => ({
          sku_id,
          qty: Number(v.qty),
          ndp: v.ndp === "" || v.ndp == null ? null : Number(v.ndp),
        })),
      });
      toast.success(`Receipt ${data.invoice_no} recorded — ${data.total_qty} units @ ${inr(data.total_value)} added to Stock Yard`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center p-4" data-testid="purchase-dialog">
      <div className="bg-canvas border border-canvas-line max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="border-b border-canvas-line px-6 py-4 flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Stock Yard · Goods Receipt</div>
            <div className="text-lg font-semibold">Receive from TVS</div>
            <div className="text-[11px] text-ink-muted mt-0.5">Stock lands in Stock Yard — push to branches via Transfer Stock afterwards.</div>
          </div>
          <div className="font-mono text-[11px] text-ink-muted text-right">
            {touched.length} SKU(s) · {totals.qty} units · {inr(totals.val)}
          </div>
        </div>
        <div className="p-6 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="TVS Invoice #" required value={invoiceNo} onChange={setInvoiceNo} mono testId="purchase-invoice-no" />
            <Field label="Invoice Date" type="date" value={invoiceDate} onChange={setInvoiceDate} mono testId="purchase-invoice-date" />
            <Field label="Supplier" value={supplier} onChange={setSupplier} testId="purchase-supplier" />
          </div>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by part no / description…"
            data-testid="purchase-search"
            className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
          />
          <div className="border border-canvas-line max-h-[45vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-canvas-panel border-b border-canvas-line sticky top-0">
                <tr>
                  <th className="px-2 py-1 text-left font-mono text-[10px] uppercase">Part #</th>
                  <th className="px-2 py-1 text-left font-mono text-[10px] uppercase">Description</th>
                  <th className="px-2 py-1 text-left font-mono text-[10px] uppercase">Received Qty</th>
                  <th className="px-2 py-1 text-left font-mono text-[10px] uppercase">NDP (₹/unit)</th>
                  <th className="px-2 py-1 text-right font-mono text-[10px] uppercase">Amount</th>
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 ? (
                  <tr><td colSpan={5} className="px-3 py-8 text-center text-ink-muted text-xs">No catalog items. Import the price list first.</td></tr>
                ) : visible.map((s) => {
                  const v = lines[s.id] || {};
                  const on = Number(v.qty) > 0;
                  const ndp = v.ndp ?? s.ndp ?? 0;
                  return (
                    <tr key={s.id} className={`border-b border-canvas-line/50 ${on ? "bg-emerald-50/40" : ""}`} data-testid={`purchase-row-${s.part_no}`}>
                      <td className="px-2 py-1 font-mono font-semibold text-xs">{s.part_no}</td>
                      <td className="px-2 py-1 text-xs">{s.description}</td>
                      <td className="px-2 py-1">
                        <input
                          type="number"
                          min={0}
                          value={v.qty ?? ""}
                          onChange={(e) => setLine(s, "qty", e.target.value)}
                          data-testid={`purchase-qty-${s.part_no}`}
                          className="w-24 px-2 py-1 bg-canvas border border-canvas-line font-mono text-sm"
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="number"
                          min={0}
                          step="0.01"
                          value={v.ndp ?? s.ndp ?? 0}
                          onChange={(e) => setLine(s, "ndp", e.target.value)}
                          data-testid={`purchase-ndp-${s.part_no}`}
                          className="w-28 px-2 py-1 bg-canvas border border-canvas-line font-mono text-sm"
                        />
                      </td>
                      <td className="px-2 py-1 font-mono text-xs text-right">
                        {on ? inr((Number(v.qty) || 0) * (Number(ndp) || 0)) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <textarea
            placeholder="Note (e.g. LR / transport reference)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm"
            rows={2}
          />
        </div>
        <div className="border-t border-canvas-line px-6 py-3 flex items-center justify-between gap-2">
          <div className="font-mono text-xs text-ink-muted">
            {touched.length > 0 ? `${touched.length} SKU(s) · ${totals.qty} units · ${inr(totals.val)}` : "No lines yet"}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 border border-canvas-line text-sm hover:border-ink">Cancel</button>
            <button onClick={submit} disabled={saving || touched.length === 0} data-testid="purchase-save" className="px-4 py-2 bg-emerald-700 text-white text-sm font-semibold hover:bg-emerald-800 disabled:opacity-50">
              {saving ? "Saving…" : `Receive ${touched.length} SKU(s)`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TransferStockDialog({ onClose, onSaved }) {
  const { user } = useAuth();
  const { branches } = useBranches({ activeOnly: true });
  const isAdmin = user?.role === "admin";
  const [fromBranch, setFromBranch] = useState(user?.branch_id || "");
  const [toBranch, setToBranch] = useState("");
  const [stockRows, setStockRows] = useState([]); // stock at source branch
  const [qtys, setQtys] = useState({});          // sku_id -> qty to move
  const [search, setSearch] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  // Load source-branch stock whenever the from-branch changes.
  useEffect(() => {
    setQtys({});
    if (!fromBranch) { setStockRows([]); return; }
    api.get("/accessories/stock", { params: { branch_id: fromBranch, include_zero: false } })
      .then((r) => setStockRows(r.data.rows || []))
      .catch((e) => toast.error(formatApiError(e)));
  }, [fromBranch]);

  const visible = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return stockRows;
    return stockRows.filter((r) =>
      (r.accessory?.part_no || "").toLowerCase().includes(s) ||
      (r.accessory?.description || "").toLowerCase().includes(s)
    );
  }, [stockRows, search]);

  const touchedCount = useMemo(
    () => Object.values(qtys).filter((v) => v !== "" && Number(v) > 0).length,
    [qtys]
  );

  const submit = async () => {
    if (!fromBranch) { toast.error("Pick the source branch"); return; }
    if (!toBranch) { toast.error("Pick the destination branch"); return; }
    if (toBranch === fromBranch) { toast.error("Source and destination must differ"); return; }
    const lines = Object.entries(qtys)
      .map(([sku_id, v]) => ({ sku_id, qty: Number(v) }))
      .filter((L) => L.qty > 0);
    if (!lines.length) { toast.error("Enter a quantity for at least one SKU"); return; }
    // Client-side sanity: never exceed available
    for (const L of lines) {
      const row = stockRows.find((r) => r.sku_id === L.sku_id);
      if (row && L.qty > Number(row.quantity || 0)) {
        toast.error(`${row.accessory?.part_no}: only ${row.quantity} available at source`);
        return;
      }
    }
    setSaving(true);
    try {
      const { data } = await api.post("/accessories/stock/transfer", {
        from_branch_id: fromBranch,
        to_branch_id: toBranch,
        note,
        lines,
      });
      toast.success(`Transferred ${data.moved.length} SKU(s) to ${branches.find((b) => b.id === toBranch)?.name || "destination"}`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center p-4" data-testid="transfer-stock-dialog">
      <div className="bg-canvas border border-canvas-line max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="border-b border-canvas-line px-6 py-4 flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Stock · Transfer</div>
            <div className="text-lg font-semibold">Transfer Stock Between Branches</div>
          </div>
          <div className="font-mono text-[11px] text-ink-muted text-right">
            {touchedCount} SKU(s) to move
          </div>
        </div>
        <div className="p-6 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-ink-muted mb-1">From (Source)</label>
              {isAdmin ? (
                <select value={fromBranch} onChange={(e) => setFromBranch(e.target.value)} data-testid="transfer-from"
                  className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm">
                  <option value="">Select…</option>
                  {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              ) : (
                <div className="px-3 py-2 border border-canvas-line bg-canvas-panel text-sm">
                  {branches.find((b) => b.id === fromBranch)?.name || "Your branch"}
                </div>
              )}
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-ink-muted mb-1">To (Destination)</label>
              <select value={toBranch} onChange={(e) => setToBranch(e.target.value)} data-testid="transfer-to"
                className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm">
                <option value="">Select…</option>
                {branches.filter((b) => b.id !== fromBranch).map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
          </div>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by part no / description…"
            data-testid="transfer-search"
            className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
          />
          <div className="border border-canvas-line max-h-[45vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-canvas-panel border-b border-canvas-line sticky top-0">
                <tr>
                  <th className="px-2 py-1 text-left font-mono text-[10px] uppercase">Part #</th>
                  <th className="px-2 py-1 text-left font-mono text-[10px] uppercase">Description</th>
                  <th className="px-2 py-1 text-left font-mono text-[10px] uppercase">Available</th>
                  <th className="px-2 py-1 text-left font-mono text-[10px] uppercase">Move Qty</th>
                </tr>
              </thead>
              <tbody>
                {!fromBranch ? (
                  <tr><td colSpan={4} className="px-3 py-8 text-center text-ink-muted text-xs">Pick a source branch to see its stock.</td></tr>
                ) : visible.length === 0 ? (
                  <tr><td colSpan={4} className="px-3 py-8 text-center text-ink-muted text-xs">No stock at this branch.</td></tr>
                ) : visible.map((r) => {
                  const touched = Number(qtys[r.sku_id] || 0) > 0;
                  return (
                    <tr key={r.sku_id} className={`border-b border-canvas-line/50 ${touched ? "bg-sky-50/60" : ""}`} data-testid={`transfer-row-${r.accessory?.part_no}`}>
                      <td className="px-2 py-1 font-mono font-semibold text-xs">{r.accessory?.part_no}</td>
                      <td className="px-2 py-1 text-xs">{r.accessory?.description}</td>
                      <td className="px-2 py-1 font-mono text-xs">{r.quantity}</td>
                      <td className="px-2 py-1">
                        <input
                          type="number"
                          min={0}
                          max={r.quantity}
                          value={qtys[r.sku_id] ?? ""}
                          onChange={(e) => setQtys((q) => ({ ...q, [r.sku_id]: e.target.value }))}
                          data-testid={`transfer-qty-${r.accessory?.part_no}`}
                          className="w-28 px-2 py-1 bg-canvas border border-canvas-line font-mono text-sm"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <textarea
            placeholder="Note (e.g. Weekly stock issue to Main Road Kakinada)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm"
            rows={2}
          />
        </div>
        <div className="border-t border-canvas-line px-6 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-canvas-line text-sm hover:border-ink">Cancel</button>
          <button onClick={submit} disabled={saving || !fromBranch || !toBranch} data-testid="transfer-save" className="px-4 py-2 bg-ink text-white text-sm font-semibold hover:bg-brand disabled:opacity-50">
            {saving ? "Moving…" : `Transfer ${touchedCount} SKU(s)`}
          </button>
        </div>
      </div>
    </div>
  );
}

function OpeningStockDialog({ onClose, onSaved }) {
  const [branchId, setBranchId] = useState("");
  const [catalog, setCatalog] = useState([]);
  const [current, setCurrent] = useState({}); // sku_id -> existing qty for chosen branch
  const [qtys, setQtys] = useState({});       // sku_id -> qty input ("" = untouched)
  const [search, setSearch] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [csvWarnings, setCsvWarnings] = useState([]);

  useEffect(() => {
    api.get("/accessories/catalog", { params: { active_only: true } })
      .then((r) => setCatalog(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  // Load existing stock for the branch so the operator sees what's already there.
  useEffect(() => {
    if (!branchId) { setCurrent({}); return; }
    api.get("/accessories/stock", { params: { branch_id: branchId, include_zero: true } })
      .then((r) => {
        const m = {};
        (r.data.rows || []).forEach((row) => { m[row.sku_id] = row.quantity; });
        setCurrent(m);
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, [branchId]);

  const visible = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return catalog;
    return catalog.filter((c) =>
      (c.part_no || "").toLowerCase().includes(s) || (c.description || "").toLowerCase().includes(s)
    );
  }, [catalog, search]);

  const touchedCount = useMemo(
    () => Object.values(qtys).filter((v) => v !== "" && v != null && Number(v) >= 0).length,
    [qtys]
  );

  const handleCsv = async (file) => {
    if (!file) return;
    const text = await file.text();
    const byPart = Object.fromEntries(catalog.map((c) => [(c.part_no || "").toUpperCase(), c.id]));
    const next = { ...qtys };
    const missing = [];
    let filled = 0;
    text.split(/\r?\n/).forEach((line, idx) => {
      const trimmed = line.trim();
      if (!trimmed) return;
      const cols = trimmed.split(",").map((x) => x.trim());
      // Skip a header row if present
      if (idx === 0 && /part/i.test(cols[0] || "") && !byPart[(cols[0] || "").toUpperCase()]) return;
      const part = (cols[0] || "").toUpperCase();
      const qty = parseFloat(cols[1] || "");
      if (!part || Number.isNaN(qty)) return;
      const skuId = byPart[part];
      if (!skuId) { missing.push(part); return; }
      next[skuId] = String(qty);
      filled += 1;
    });
    setQtys(next);
    setCsvWarnings(missing.slice(0, 20));
    toast.success(`CSV applied: ${filled} rows filled${missing.length ? ` · ${missing.length} part numbers not in catalog` : ""}`);
  };

  const submit = async () => {
    if (!branchId) { toast.error("Pick a branch"); return; }
    const lines = Object.entries(qtys)
      .filter(([, v]) => v !== "" && v != null)
      .map(([sku_id, v]) => ({ sku_id, quantity: Number(v) }))
      .filter((L) => !Number.isNaN(L.quantity) && L.quantity >= 0);
    if (!lines.length) { toast.error("Enter a quantity for at least one SKU"); return; }
    setSaving(true);
    try {
      await api.post("/accessories/stock/opening", { branch_id: branchId, lines, note });
      toast.success(`Opening stock saved for ${lines.length} SKU(s)`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center p-4" data-testid="opening-stock-dialog">
      <div className="bg-canvas border border-canvas-line max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="border-b border-canvas-line px-6 py-4 flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Stock</div>
            <div className="text-lg font-semibold">Opening Stock Entry</div>
          </div>
          <div className="font-mono text-[11px] text-ink-muted text-right">
            {touchedCount} SKU(s) to save
          </div>
        </div>
        <div className="p-6 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <BranchSelect value={branchId} onChange={setBranchId} testId="opening-branch" required />
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-ink-muted mb-1">
                Bulk fill from CSV (part_no, qty)
              </label>
              <input
                type="file"
                accept=".csv"
                data-testid="opening-csv-file"
                onChange={(e) => { handleCsv(e.target.files?.[0]); e.target.value = ""; }}
                className="text-xs"
              />
            </div>
          </div>
          {csvWarnings.length > 0 && (
            <div className="border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs text-amber-800" data-testid="opening-csv-warnings">
              Not in catalog (skipped): {csvWarnings.join(", ")}
            </div>
          )}
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by part no / description…"
            data-testid="opening-search"
            className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
          />
          <div className="border border-canvas-line max-h-[45vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-canvas-panel border-b border-canvas-line sticky top-0">
                <tr>
                  <th className="px-2 py-1 text-left font-mono text-[10px] uppercase">Part #</th>
                  <th className="px-2 py-1 text-left font-mono text-[10px] uppercase">Description</th>
                  <th className="px-2 py-1 text-left font-mono text-[10px] uppercase">Current</th>
                  <th className="px-2 py-1 text-left font-mono text-[10px] uppercase">Opening Qty</th>
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 ? (
                  <tr><td colSpan={4} className="px-3 py-8 text-center text-ink-muted text-xs">No catalog items. Import the price list first.</td></tr>
                ) : visible.map((s) => {
                  const touched = qtys[s.id] !== "" && qtys[s.id] != null;
                  return (
                    <tr key={s.id} className={`border-b border-canvas-line/50 ${touched ? "bg-emerald-50/40" : ""}`} data-testid={`opening-row-${s.part_no}`}>
                      <td className="px-2 py-1 font-mono font-semibold text-xs">{s.part_no}</td>
                      <td className="px-2 py-1 text-xs">{s.description}</td>
                      <td className="px-2 py-1 font-mono text-xs text-ink-muted">{current[s.id] ?? 0}</td>
                      <td className="px-2 py-1">
                        <input
                          type="number"
                          min={0}
                          value={qtys[s.id] ?? ""}
                          placeholder={String(current[s.id] ?? 0)}
                          onChange={(e) => setQtys((q) => ({ ...q, [s.id]: e.target.value }))}
                          data-testid={`opening-qty-${s.part_no}`}
                          className="w-28 px-2 py-1 bg-canvas border border-canvas-line font-mono text-sm"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <textarea
            placeholder="Note (e.g. Stock audit as of 20-Feb-2026)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm"
            rows={2}
          />
        </div>
        <div className="border-t border-canvas-line px-6 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-canvas-line text-sm hover:border-ink">Cancel</button>
          <button onClick={submit} disabled={saving || !branchId} data-testid="opening-save" className="px-4 py-2 bg-ink text-white text-sm font-semibold hover:bg-brand disabled:opacity-50">
            {saving ? "Saving…" : `Save ${touchedCount} SKU(s)`}
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================
// Retail Bill
// =============================================================
function RetailBillTab() {
  return <InvoiceComposer mode="retail" />;
}

function B2BBillTab() {
  return <InvoiceComposer mode="b2b" />;
}

function SkuPicker({ catalog, value, priceField, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const selected = catalog.find((s) => s.id === value);
  const needle = q.trim().toLowerCase();
  const matches = needle
    ? catalog.filter((s) => `${s.part_no} ${s.description}`.toLowerCase().includes(needle))
    : catalog;
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => { setOpen((o) => !o); setQ(""); }}
        data-testid={testId}
        className="w-full px-2 py-1 bg-canvas border border-canvas-line text-sm text-left truncate"
      >
        {selected
          ? `${selected.part_no} · ${selected.description} (${inr(selected[priceField])})`
          : "Pick SKU…"}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute z-40 left-0 mt-1 border border-canvas-line bg-canvas shadow-lg min-w-[360px]">
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search part no / description…"
              data-testid={`${testId}-search`}
              className="w-full px-2 py-1.5 border-b border-canvas-line text-sm focus:outline-none"
            />
            <div className="max-h-56 overflow-y-auto">
              {matches.length === 0 ? (
                <div className="px-2 py-3 text-xs text-ink-muted">No match for “{q}”.</div>
              ) : (
                matches.slice(0, 50).map((s) => (
                  <button
                    type="button"
                    key={s.id}
                    onClick={() => { onChange(s.id); setOpen(false); setQ(""); }}
                    data-testid={`${testId}-option-${s.part_no}`}
                    className={`w-full text-left px-2 py-1.5 text-sm hover:bg-canvas-panel flex justify-between gap-2 ${s.id === value ? "bg-canvas-panel" : ""}`}
                  >
                    <span className="truncate"><span className="font-mono">{s.part_no}</span> · {s.description}</span>
                    <span className="font-mono text-xs text-ink-muted whitespace-nowrap">{inr(s[priceField])}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function InvoiceComposer({ mode }) {
  const isB2B = mode === "b2b";
  const { user } = useAuth();
  const { branches } = useBranches({ activeOnly: true });
  const [catalog, setCatalog] = useState([]);
  const [adBranches, setAdBranches] = useState([]);
  const [branchId, setBranchId] = useState(user?.branch_id || "");
  const [subDealerId, setSubDealerId] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [gstType, setGstType] = useState("intra");
  const [collectionMode, setCollectionMode] = useState("free_bundled");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [paymentRef, setPaymentRef] = useState("");
  const [vinInput, setVinInput] = useState("");
  const [linkedVehicle, setLinkedVehicle] = useState(null); // {vehicle_id, vin, package_name, ...}
  const [vinLookupBusy, setVinLookupBusy] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerId, setCustomerId] = useState(null);
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState([{ sku_id: "", qty: 1, unit_price: "", discount: 0 }]);
  const [invoices, setInvoices] = useState([]);
  const [saving, setSaving] = useState(false);
  const [lastInvoice, setLastInvoice] = useState(null);

  const loadInvoices = useCallback(async () => {
    try {
      const params = { invoice_type: isB2B ? "b2b_sub_dealer" : "retail", limit: 50 };
      const { data } = await api.get("/accessories/invoices", { params });
      setInvoices(data.rows || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [isB2B]);

  useEffect(() => {
    api.get("/accessories/catalog", { params: { active_only: true } })
      .then((r) => setCatalog(r.data)).catch((e) => toast.error(formatApiError(e)));
    if (isB2B) {
      api.get("/sub-dealers/accounts")
        .then((r) => setAdBranches(r.data.accounts?.map((a) => a.branch) || []))
        .catch(() => {});
    }
    loadInvoices();
  }, [isB2B, loadInvoices]);

  const lookupVin = async () => {
    const v = (vinInput || "").trim().toUpperCase();
    if (v.length < 3) { toast.error("Enter at least 3 characters of the VIN"); return; }
    setVinLookupBusy(true);
    try {
      const { data } = await api.get("/accessories/vehicle-lookup", { params: { vin: v } });
      setLinkedVehicle(data);
      setVinInput(data.vin || v);
      if (data.customer_name) setCustomerName(data.customer_name);
      if (data.customer_phone) setCustomerPhone(data.customer_phone);
      setCustomerId(data.customer_id || null);
      // A vehicle-linked bill is a post-sale task — default to FREE (bundled).
      setCollectionMode("free_bundled");
      if (data.customer_name) {
        toast.success(`Linked to ${data.package_name || "vehicle"} · customer auto-filled`);
      } else {
        toast.success("Vehicle found — no linked customer on record");
      }
    } catch (e) {
      toast.error(formatApiError(e));
      setLinkedVehicle(null);
    } finally { setVinLookupBusy(false); }
  };

  const clearVehicle = () => {
    setLinkedVehicle(null);
    setVinInput("");
    setCustomerId(null);
    // Free issue only makes sense when linked to a vehicle sale — reset to Paid
    // whenever we're back to a standalone accessory bill.
    if (collectionMode === "free_bundled") setCollectionMode("paid");
  };

  // If the user unlinks the vehicle mid-flow, snap Free back to Paid so a
  // stray FREE flag never travels with a walk-in accessory sale.
  useEffect(() => {
    if (!linkedVehicle && collectionMode === "free_bundled") {
      setCollectionMode("paid");
    }
  }, [linkedVehicle, collectionMode]);

  const isFree = collectionMode === "free_bundled";
  // FREE / bundled is only meaningful when this bill is a post-sale issue
  // against a delivered vehicle — hide the toggle for walk-in / standalone sales.
  const canBeFree = !!linkedVehicle;

  const skuMap = useMemo(() => Object.fromEntries(catalog.map((s) => [s.id, s])), [catalog]);
  const priceField = isB2B ? "ad_mrp" : "mrp";

  const totals = useMemo(() => {
    let taxable = 0, gst = 0, grand = 0;
    lines.forEach((L) => {
      const sku = skuMap[L.sku_id];
      if (!sku) return;
      const qty = Number(L.qty) || 0;
      const unit = Number(L.unit_price) || Number(sku[priceField]) || 0;
      const disc = isB2B ? 0 : (Number(L.discount) || 0);
      const gross = unit * qty - disc;
      if (gross <= 0) return;
      const gstPct = Number(sku.gst_pct) || 18;
      const t = gross / (1 + gstPct / 100);
      taxable += t;
      gst += gross - t;
      grand += gross;
    });
    return { taxable: Math.round(taxable * 100) / 100, gst: Math.round(gst * 100) / 100, grand: Math.round(grand * 100) / 100 };
  }, [lines, skuMap, priceField, isB2B]);

  const setLine = (i, k, v) => setLines((L) => L.map((row, j) => j === i ? { ...row, [k]: v } : row));
  const addLine = () => setLines((L) => [...L, { sku_id: "", qty: 1, unit_price: "", discount: 0 }]);
  const rmLine = (i) => setLines((L) => L.filter((_, j) => j !== i));

  const submit = async () => {
    const cleaned = lines.filter((L) => L.sku_id && Number(L.qty) > 0);
    if (!cleaned.length) { toast.error("Add at least one line"); return; }
    if (isB2B && !subDealerId) { toast.error("Select the sub-dealer"); return; }
    if (!branchId) { toast.error("Select the source branch"); return; }
    setSaving(true);
    try {
      const body = isB2B
        ? {
            sub_dealer_branch_id: subDealerId,
            source_branch_id: branchId,
            date, gst_type: gstType, notes,
            lines: cleaned.map((L) => ({
              sku_id: L.sku_id,
              qty: Number(L.qty),
              unit_price: L.unit_price === "" ? null : Number(L.unit_price),
            })),
          }
        : {
            branch_id: branchId,
            vehicle_id: linkedVehicle?.vehicle_id || null,
            customer_id: customerId || null,
            customer_name: customerName,
            customer_phone: customerPhone,
            collection_mode: collectionMode,
            date, gst_type: gstType,
            payment_method: isFree ? "none" : paymentMethod,
            payment_reference: isFree ? "" : paymentRef,
            notes,
            lines: cleaned.map((L) => ({
              sku_id: L.sku_id,
              qty: Number(L.qty),
              unit_price: L.unit_price === "" ? null : Number(L.unit_price),
              discount: Number(L.discount) || 0,
            })),
          };
      const { data } = await api.post(`/accessories/invoices/${isB2B ? "b2b" : "retail"}`, body);
      toast.success(`Invoice ${data.invoice_no} generated (${inr(data.grand_total)})`);
      setLastInvoice(data);
      setLines([{ sku_id: "", qty: 1, unit_price: "", discount: 0 }]);
      setCustomerName(""); setCustomerPhone(""); setPaymentRef(""); setNotes("");
      clearVehicle();
      loadInvoices();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="border border-canvas-line p-4 space-y-3">
        {!isB2B && (
          <div className="border border-canvas-line bg-canvas-panel/40 p-3" data-testid="vin-link-block">
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex-1 min-w-[220px]">
                <label className="block font-mono text-[10px] uppercase tracking-widest text-ink-muted mb-1">
                  Link to Vehicle (optional) — VIN
                </label>
                <input
                  value={vinInput}
                  onChange={(e) => setVinInput(e.target.value.toUpperCase())}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); lookupVin(); } }}
                  placeholder="Chassis / VIN (last digits work)"
                  data-testid="invoice-vin-input"
                  className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm font-mono focus:outline-none focus:border-ink"
                />
              </div>
              <button
                onClick={lookupVin}
                disabled={vinLookupBusy}
                data-testid="invoice-vin-lookup"
                className="px-3 py-2 border border-canvas-line text-xs font-mono uppercase tracking-wider hover:border-ink disabled:opacity-40"
              >
                {vinLookupBusy ? "…" : "Lookup"}
              </button>
              {linkedVehicle && (
                <button onClick={clearVehicle} data-testid="invoice-vin-clear"
                  className="px-3 py-2 border border-rose-300 text-rose-700 text-xs font-mono uppercase tracking-wider hover:bg-rose-50">
                  Unlink
                </button>
              )}
            </div>
            {linkedVehicle && (
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs" data-testid="invoice-vin-badge">
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-ink text-white font-mono text-[10px] uppercase">
                  VIN {linkedVehicle.vin}
                </span>
                {linkedVehicle.package_name && (
                  <span className="px-2 py-1 border border-canvas-line font-mono text-[10px] uppercase">{linkedVehicle.package_name}</span>
                )}
                {linkedVehicle.color && (
                  <span className="px-2 py-1 border border-canvas-line font-mono text-[10px] uppercase">{linkedVehicle.color}</span>
                )}
                {linkedVehicle.sale_date && (
                  <span className="text-ink-muted">Sold {linkedVehicle.sale_date}</span>
                )}
              </div>
            )}
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-ink-muted mb-1">
              {isB2B ? "Source Branch" : "Branch"}
            </label>
            <select value={branchId} onChange={(e) => setBranchId(e.target.value)} data-testid="invoice-branch"
              className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm">
              <option value="">Select…</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          {isB2B ? (
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-ink-muted mb-1">Sub-Dealer</label>
              <select value={subDealerId} onChange={(e) => setSubDealerId(e.target.value)} data-testid="invoice-sub-dealer"
                className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm">
                <option value="">Select sub-dealer…</option>
                {adBranches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          ) : (
            <>
              <Field label="Customer Name" value={customerName} onChange={setCustomerName} testId="invoice-customer-name" />
              <Field label="Phone" value={customerPhone} onChange={setCustomerPhone} mono testId="invoice-customer-phone" />
            </>
          )}
          <Field label="Date" type="date" value={date} onChange={setDate} mono />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SelectField label="GST Type" value={gstType} onChange={setGstType} options={["intra", "inter"]} />
          {!isB2B && (
            <>
              {canBeFree && (
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-widest text-ink-muted mb-1">Collection</label>
                  <div className="flex border border-canvas-line" data-testid="invoice-collection-toggle">
                    <button
                      type="button"
                      onClick={() => setCollectionMode("paid")}
                      data-testid="collection-paid"
                      className={`flex-1 px-2 py-2 text-xs font-mono uppercase ${!isFree ? "bg-ink text-white" : "bg-canvas text-ink-muted hover:text-ink"}`}
                    >
                      Paid
                    </button>
                    <button
                      type="button"
                      onClick={() => setCollectionMode("free_bundled")}
                      data-testid="collection-free"
                      className={`flex-1 px-2 py-2 text-xs font-mono uppercase ${isFree ? "bg-emerald-700 text-white" : "bg-canvas text-ink-muted hover:text-ink"}`}
                    >
                      Free
                    </button>
                  </div>
                </div>
              )}
              {!isFree && (
                <>
                  <SelectField label="Payment Method" value={paymentMethod} onChange={setPaymentMethod} options={["cash", "upi", "card", "online", "mixed"]} testId="invoice-payment-method" />
                  <Field label="Payment Reference" value={paymentRef} onChange={setPaymentRef} mono placeholder="UTR / txn id" />
                </>
              )}
            </>
          )}
        </div>
        {!isB2B && isFree && (
          <div className="border border-emerald-200 bg-emerald-50/50 px-3 py-2 text-xs text-emerald-800" data-testid="free-mode-hint">
            Free issue — no cash collected. GST is still recorded at full MRP value for GST posting.
          </div>
        )}
        <div className="border border-canvas-line">
          <table className="w-full text-sm">
            <thead className="bg-canvas-panel border-b border-canvas-line">
              <tr>
                <th className="px-2 py-1 text-left font-mono text-[10px] uppercase">Accessory</th>
                <th className="px-2 py-1 text-left font-mono text-[10px] uppercase">Qty</th>
                <th className="px-2 py-1 text-left font-mono text-[10px] uppercase">Unit (₹, override)</th>
                {!isB2B && <th className="px-2 py-1 text-left font-mono text-[10px] uppercase">Disc (₹)</th>}
                <th className="px-2 py-1 text-right font-mono text-[10px] uppercase">Total (incl. GST)</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((L, i) => {
                const sku = skuMap[L.sku_id];
                const unit = Number(L.unit_price) || Number(sku?.[priceField]) || 0;
                const disc = isB2B ? 0 : (Number(L.discount) || 0);
                const total = unit * Number(L.qty || 0) - disc;
                return (
                  <tr key={i} className="border-b border-canvas-line/50">
                    <td className="px-2 py-1">
                      <SkuPicker
                        catalog={catalog}
                        value={L.sku_id}
                        priceField={priceField}
                        onChange={(v) => setLine(i, "sku_id", v)}
                        testId={`invoice-line-sku-${i}`}
                      />
                    </td>
                    <td className="px-2 py-1">
                      <input type="number" min={1} value={L.qty} onChange={(e) => setLine(i, "qty", e.target.value)} data-testid={`invoice-line-qty-${i}`}
                        className="w-20 px-2 py-1 bg-canvas border border-canvas-line font-mono text-sm" />
                    </td>
                    <td className="px-2 py-1">
                      <input type="number" min={0} value={L.unit_price} placeholder={sku ? String(sku[priceField]) : ""}
                        onChange={(e) => setLine(i, "unit_price", e.target.value)}
                        className="w-28 px-2 py-1 bg-canvas border border-canvas-line font-mono text-sm" />
                    </td>
                    {!isB2B && (
                      <td className="px-2 py-1">
                        <input type="number" min={0} value={L.discount} onChange={(e) => setLine(i, "discount", e.target.value)}
                          className="w-24 px-2 py-1 bg-canvas border border-canvas-line font-mono text-sm" />
                      </td>
                    )}
                    <td className="px-2 py-1 font-mono text-right">{inr(total)}</td>
                    <td className="px-2 py-1 text-right">
                      <button onClick={() => rmLine(i)} className="text-rose-700 hover:bg-rose-50 p-1"><Trash size={12} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="px-2 py-2 border-t border-canvas-line">
            <button onClick={addLine} className="px-2 py-1 border border-canvas-line text-[10px] font-mono uppercase tracking-wider hover:border-ink">+ Add line</button>
          </div>
        </div>
        <textarea placeholder="Notes…" value={notes} onChange={(e) => setNotes(e.target.value)}
          className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" rows={2} />
        <div className="flex items-center justify-between border-t border-canvas-line pt-3">
          <div className="text-sm font-mono">
            <span className="text-ink-muted">Taxable</span> {inr(totals.taxable)} · <span className="text-ink-muted">GST</span> {inr(totals.gst)} · <span className="text-ink-muted font-semibold">Grand</span> <b>{inr(totals.grand)}</b>
          </div>
          <button onClick={submit} disabled={saving} data-testid="invoice-submit"
            className="px-4 py-2 bg-ink text-white text-sm font-semibold hover:bg-brand disabled:opacity-50">
            {saving ? "Saving…" : isB2B ? "Post B2B Invoice" : isFree ? "Issue Free (Post-Sale)" : "Bill Customer"}
          </button>
        </div>
        {lastInvoice && (
          <div className="border border-emerald-200 bg-emerald-50/40 p-3 text-xs flex items-center justify-between gap-2" data-testid="invoice-last">
            <div>
              <b>{lastInvoice.invoice_no}</b> · Grand Total <b>{inr(lastInvoice.grand_total)}</b>
              {lastInvoice.collection_mode === "free_bundled" ? <> · <b className="text-emerald-700">FREE — no cash collected</b></> : null}
              {isB2B && lastInvoice.ledger_entry_id ? <> · Sub-dealer ledger entry posted</> : null}
            </div>
            <button
              onClick={() => printAccessoryInvoice(lastInvoice.id)}
              data-testid="invoice-print-last"
              className="px-3 py-1.5 border border-canvas-line text-[10px] font-mono uppercase tracking-wider hover:border-ink whitespace-nowrap"
            >
              Print Copy
            </button>
          </div>
        )}
      </div>

      <div className="border border-canvas-line">
        <div className="px-3 py-2 border-b border-canvas-line font-mono text-[10px] uppercase tracking-widest text-ink-muted">
          Recent {isB2B ? "B2B" : "Retail"} Invoices ({invoices.length})
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-canvas-panel border-b border-canvas-line">
              <tr>
                {["Invoice #", "Date", isB2B ? "Sub-Dealer" : "Customer", "Lines", "Grand", ""].map((h) => (
                  <th key={h} className="px-3 py-2 font-mono text-[10px] uppercase text-left">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-ink-muted text-xs">No invoices yet.</td></tr>
              ) : invoices.map((inv) => (
                <tr key={inv.id} className="border-b border-canvas-line/50" data-testid={`invoice-row-${inv.invoice_no}`}>
                  <td className="px-3 py-2 font-mono font-semibold">
                    {inv.invoice_no}
                    {inv.collection_mode === "free_bundled" && (
                      <span className="ml-2 inline-block px-1.5 py-0.5 bg-emerald-100 text-emerald-800 text-[9px] font-mono uppercase">FREE</span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px]">{inv.date}</td>
                  <td className="px-3 py-2">
                    {isB2B
                      ? (branches.find((b) => b.id === inv.sub_dealer_branch_id)?.name || inv.sub_dealer_branch_id)
                      : (inv.customer_name || "Walk-in")}
                    {inv.vehicle_id && (
                      <div className="font-mono text-[10px] text-ink-muted">linked to VIN</div>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono">{(inv.lines || []).length}</td>
                  <td className="px-3 py-2 font-mono font-semibold">{inr(inv.grand_total)}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => printAccessoryInvoice(inv.id)}
                      data-testid={`invoice-print-${inv.invoice_no}`}
                      className="px-2 py-1 border border-canvas-line text-[10px] font-mono uppercase tracking-wider hover:border-ink"
                    >
                      Print
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// =============================================================
// Reports
// =============================================================
function ReportsTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { branches } = useBranches({ activeOnly: true });
  const [branchId, setBranchId] = useState(user?.branch_id || "");
  const [category, setCategory] = useState("");
  const [data, setData] = useState({ branches: [], lines: [], grand_total: 0 });

  const load = useCallback(async () => {
    try {
      const params = {};
      if (branchId) params.branch_id = branchId;
      if (category) params.category = category;
      const { data } = await api.get("/accessories/reports/stock-valuation", { params });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [branchId, category]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const lines = data.lines || [];
    if (!lines.length) { toast.error("No stock rows to export"); return; }
    const headers = ["branch", "part_no", "description", "category", "units", "ndp", "value"];
    const rows = lines.map((l) => [
      l.branch_name || l.branch_id, l.part_no, l.description, l.category, l.units, l.ndp, l.value,
    ]);
    rows.push(["GRAND TOTAL", "", "", "", lines.reduce((a, l) => a + (l.units || 0), 0), "", data.grand_total]);
    const csv = [headers, ...rows]
      .map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `stock-valuation-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        {isAdmin && (
          <select value={branchId} onChange={(e) => setBranchId(e.target.value)} data-testid="report-branch"
            className="px-3 py-2 bg-canvas border border-canvas-line text-sm">
            <option value="">All branches</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
        <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="report-category"
          className="px-3 py-2 bg-canvas border border-canvas-line text-sm">
          <option value="">All categories</option>
          {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button
          onClick={exportCsv}
          data-testid="report-export-csv"
          className="ml-auto px-3 py-2 border border-canvas-line text-xs font-mono uppercase tracking-wider hover:border-ink flex items-center gap-1.5"
        >
          <DownloadSimple size={12} /> Export CSV
        </button>
      </div>
      <div className="border border-canvas-line bg-canvas-panel/40 p-4">
        <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">Grand Total Stock Value (@ NDP)</div>
        <div className="text-3xl font-bold font-mono mt-1" data-testid="report-grand-total">{inr(data.grand_total)}</div>
      </div>
      <div className="border border-canvas-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-canvas-panel border-b border-canvas-line">
            <tr>
              {["Branch", "SKUs", "Units", "Value"].map((h) => (
                <th key={h} className="px-3 py-2 font-mono text-[10px] uppercase text-left">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(data.branches || []).length === 0 ? (
              <tr><td colSpan={4} className="px-3 py-10 text-center text-ink-muted text-xs">No stock recorded yet.</td></tr>
            ) : (data.branches || []).map((b) => (
              <tr key={b.branch_id} className="border-b border-canvas-line/50">
                <td className="px-3 py-2">{b.branch_name || b.branch_id}</td>
                <td className="px-3 py-2 font-mono">{b.skus}</td>
                <td className="px-3 py-2 font-mono">{b.units}</td>
                <td className="px-3 py-2 font-mono font-semibold">{inr(b.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Per-SKU detail: branch × SKU with count and amount */}
      <div className="border border-canvas-line overflow-x-auto" data-testid="report-sku-detail">
        <div className="px-3 py-2 border-b border-canvas-line font-mono text-[10px] uppercase tracking-[0.25em] text-ink-muted">
          Stock by SKU ({(data.lines || []).length} rows)
        </div>
        <table className="w-full text-sm">
          <thead className="bg-canvas-panel border-b border-canvas-line">
            <tr>
              {["Branch", "Part No", "Description", "Category", "Count", "NDP ₹", "Amount"].map((h) => (
                <th key={h} className="px-3 py-2 font-mono text-[10px] uppercase text-left">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(data.lines || []).length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-10 text-center text-ink-muted text-xs">No stock rows for the selected filters.</td></tr>
            ) : (data.lines || []).map((l, i) => (
              <tr key={`${l.branch_id}-${l.part_no}-${i}`} className="border-b border-canvas-line/50" data-testid={`report-line-${l.part_no}`}>
                <td className="px-3 py-2">{l.branch_name || l.branch_id}</td>
                <td className="px-3 py-2 font-mono">{l.part_no}</td>
                <td className="px-3 py-2">{l.description}</td>
                <td className="px-3 py-2 font-mono text-xs uppercase text-ink-muted">{l.category}</td>
                <td className="px-3 py-2 font-mono">{l.units}</td>
                <td className="px-3 py-2 font-mono">{inr(l.ndp)}</td>
                <td className="px-3 py-2 font-mono font-semibold">{inr(l.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============================================================
// Small helper inputs
// =============================================================
function Field({ label, value, onChange, type = "text", required, placeholder = "", mono, testId }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-widest text-ink-muted mb-1">
        {label}{required ? <span className="text-rose-600"> *</span> : null}
      </label>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        data-testid={testId}
        className={`w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink ${mono ? "font-mono" : ""}`}
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-widest text-ink-muted mb-1">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
      >
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
